"use client"

import clsx from "clsx"
import { format } from "date-fns"
import { enUS, ja, ko } from "react-day-picker/locale"
import { useState } from "react"
import { TIcon } from "../display/TIcon"
import { Calendar } from "../shadcn/calendar"
import { Popover, PopoverContent, PopoverTrigger } from "../shadcn/popover"

/**
 * @description 날짜 선택 캘린더 팝업. 클릭하면 캘린더가 열림
 * @example
 * ```tsx
 * <TDatePicker value={selectedDate} onChange={setSelectedDate} />
 * ```
 */
interface TDatePickerProps {
	/** 선택된 날짜 */
	value?: Date
	/**
	 * 입력 필드 크기
	 * @default "medium"
	 * @ai-style "small" - 높이 32px
	 * @ai-style "medium" - 높이 40px
	 */
	size?: "small" | "medium"
	/** 비활성화 여부 */
	disabled?: boolean
	/** 에러 상태 표시 */
	error?: boolean
	/** 과거 날짜 선택 불가 */
	disablePast?: boolean
	/** 미래 날짜 선택 불가 */
	disableFuture?: boolean
	/**
	 * 캘린더 로케일
	 * @default "en"
	 */
	currentLang?: "ko" | "ja" | "en"
	/** 추가 CSS 클래스 */
	className?: string
	/**
	 * 날짜 미선택 시 표시할 텍스트
	 * @default "날짜 선택"
	 */
	placeholder?: string
	/** 팝업 가로 정렬 */
	align?: "start" | "center" | "end"
	/** 팝업 표시 방향 */
	side?: "top" | "right" | "bottom" | "left"
	/** 팝업 가로 정렬 오프셋 (px) */
	alignOffset?: number
	/** 팝업 방향 오프셋 (px) */
	sideOffset?: number
	/** 날짜 선택 시 콜백 */
	onChange?: (date: Date) => void
}

export const TDatePicker = (props: TDatePickerProps) => {
	const {
		value,
		size = "medium",
		disabled,
		error,
		disablePast,
		disableFuture,
		currentLang = "en",
		className,
		placeholder = "날짜 선택",
		align = "start",
		side = "bottom",
		alignOffset = 0,
		sideOffset = 4,
		onChange
	} = props
	const [open, setOpen] = useState<boolean>(false)
	const localeMap = {
		ko: ko,
		ja: ja,
		en: enUS
	}
	const selectedLocale = localeMap[currentLang]

	return (
		<Popover onOpenChange={setOpen} open={open}>
			<PopoverTrigger asChild>
				<div className={clsx("TDate", size, className)} data-open={open} data-disabled={disabled} data-error={error}>
					<label className={clsx("label", !value && "text-text-placeholder")}>{value ? format(value, "yyyy.MM.dd") : placeholder}</label>
					<div className="ml-2 mr-3">
						<TIcon size={16} name="calendar" />
					</div>
				</div>
			</PopoverTrigger>
			<PopoverContent className="min-w-64" align={align} side={side} alignOffset={alignOffset} sideOffset={sideOffset}>
				<Calendar
					mode="single"
					locale={selectedLocale}
					defaultMonth={value || new Date()}
					selected={value}
					onSelect={(date) => {
						if (date) {
							onChange?.(date)
							setOpen(false)
						}
					}}
					disabled={(date: Date) => {
						if (disablePast && date < new Date(new Date().toDateString())) {
							return true
						}
						if (disableFuture && date > new Date(new Date().toDateString())) {
							return true
						}
						return false
					}}
					formatters={{
						formatWeekdayName: (date) => format(date, "EEE", { locale: selectedLocale }),
						formatCaption: (date) =>
							currentLang === "ko"
								? format(date, "yyyy년 M월", { locale: ko })
								: currentLang === "ja"
									? format(date, "yyyy年M月", { locale: ja })
									: format(date, "LLLL yyyy", { locale: enUS })
					}}
				/>
			</PopoverContent>
		</Popover>
	)
}

TDatePicker.displayName = "TDatePicker"
